import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Input, Rate } from "antd";
import { CheckCircleFilled, FrownFilled } from "@ant-design/icons";
import {
    submitPublicAppointmentCare,
    CareSurveyRatings,
    PublicCareErrorType,
} from "../../api/AppointmentCare";

const { TextArea } = Input;

/**
 * Trang khảo sát công khai cho khách hàng: /khao-sat/:id
 *
 * Khách mở từ link gửi qua Zalo/SMS nên trang phải chạy được khi CHƯA đăng nhập
 * (đặt ngoài PermissionRoute, không dùng Layout admin).
 *
 * API công khai chỉ cho phép GỬI, không có endpoint đọc lịch hẹn, nên trang không
 * hiển thị được tên khách / dịch vụ — chỉ chào chung.
 */

const RATING_FIELDS: { key: keyof CareSurveyRatings; title: string; errorKey: string }[] = [
    { key: "quality", title: "Chất lượng dịch vụ", errorKey: "ratings.quality" },
    { key: "staff_attitude", title: "Thái độ nhân viên", errorKey: "ratings.staff_attitude" },
    { key: "doctor_ktv", title: "Bác sĩ / Kỹ thuật viên", errorKey: "ratings.doctor_ktv" },
    { key: "facilities", title: "Cơ sở vật chất", errorKey: "ratings.facilities" },
];

const EMPTY_RATINGS: CareSurveyRatings = { quality: 0, staff_attitude: 0, doctor_ktv: 0, facilities: 0 };

type Screen = "form" | "done" | "already_cared" | "not_found";

export default function AppointmentSurvey() {
    const { id } = useParams();
    const [ratings, setRatings] = useState<CareSurveyRatings>(EMPTY_RATINGS);
    const [careNote, setCareNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [screen, setScreen] = useState<Screen>("form");
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [formError, setFormError] = useState<string | null>(null);

    const handleSubmit = async () => {
        if (submitting) return; // chặn double-submit: API chỉ chặn được lần 2 sau khi lần 1 đã ghi xong

        const missing = RATING_FIELDS.some((f) => !ratings[f.key]);
        if (missing) {
            setFormError("Vui lòng chấm sao cho đủ 4 mục bên trên.");
            return;
        }
        if (!careNote.trim()) {
            setFormError("Vui lòng để lại đôi lời nhận xét giúp chúng tôi nhé.");
            return;
        }

        setFormError(null);
        setFieldErrors({});
        setSubmitting(true);
        const res = await submitPublicAppointmentCare(id || "", {
            care_note: careNote.trim(),
            ratings,
        });
        setSubmitting(false);

        if (res.status) {
            setScreen("done");
            return;
        }

        const type: PublicCareErrorType = res.errorType || "network";
        if (type === "not_found") {
            setScreen("not_found");
        } else if (type === "already_cared") {
            setScreen("already_cared");
        } else if (type === "validation") {
            setFieldErrors(res.fieldErrors || {});
            setFormError(res.msg || "Thông tin chưa hợp lệ, vui lòng kiểm tra lại.");
        } else {
            setFormError("Không gửi được đánh giá. Vui lòng kiểm tra kết nối mạng và thử lại.");
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 py-8 px-4">

            <div className="mx-auto w-full max-w-lg">

                <div className="text-center mb-6">
                    <img src="/images/logo.png" alt="logo" className="h-[52px] object-contain mx-auto" />
                </div>

                {screen === "form" && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">

                        <h1 className="text-xl font-bold text-slate-800 text-center">
                            Cảm ơn quý khách đã sử dụng dịch vụ
                        </h1>

                        <p className="text-sm text-slate-500 text-center mt-2 mb-6">
                            Quý khách vui lòng dành ít phút đánh giá để chúng tôi phục vụ tốt hơn.
                        </p>

                        <div className="divide-y divide-slate-100">
                            {RATING_FIELDS.map((item) => (
                                <div key={item.key} className="py-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                        <span className="text-slate-700 font-medium">{item.title}</span>
                                        <Rate
                                            style={{ fontSize: 30 }}
                                            value={Number(ratings[item.key]) || 0}
                                            onChange={(v) => setRatings((prev) => ({ ...prev, [item.key]: v }))}
                                        />
                                    </div>
                                    {fieldErrors[item.errorKey] && (
                                        <div className="text-red-500 text-xs mt-1">{fieldErrors[item.errorKey]}</div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="mt-5">
                            <div className="font-medium text-slate-700 mb-2">Nhận xét của quý khách</div>
                            <TextArea
                                rows={4}
                                placeholder="Quý khách hài lòng hoặc chưa hài lòng điều gì?"
                                value={careNote}
                                onChange={(e) => setCareNote(e.target.value)}
                                maxLength={2000}
                                showCount
                            />
                            {fieldErrors["care_note"] && (
                                <div className="text-red-500 text-xs mt-1">{fieldErrors["care_note"]}</div>
                            )}
                        </div>

                        {formError && (
                            <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">
                                {formError}
                            </div>
                        )}

                        <Button
                            type="primary"
                            size="large"
                            block
                            className="mt-6"
                            loading={submitting}
                            onClick={handleSubmit}
                        >
                            Gửi đánh giá
                        </Button>

                    </div>
                )}

                {screen === "done" && (
                    <ResultCard
                        icon={<CheckCircleFilled className="text-5xl text-emerald-500" />}
                        title="Đã ghi nhận đánh giá"
                        desc="Cảm ơn quý khách đã dành thời gian phản hồi. Chúng tôi sẽ tiếp tục cải thiện chất lượng dịch vụ."
                    />
                )}

                {screen === "already_cared" && (
                    <ResultCard
                        icon={<CheckCircleFilled className="text-5xl text-blue-500" />}
                        title="Lịch hẹn này đã được đánh giá"
                        desc="Mỗi lần sử dụng dịch vụ chỉ đánh giá được một lần. Cảm ơn quý khách đã phản hồi."
                    />
                )}

                {screen === "not_found" && (
                    <ResultCard
                        icon={<FrownFilled className="text-5xl text-slate-400" />}
                        title="Không tìm thấy lịch hẹn"
                        desc="Đường dẫn có thể đã hết hiệu lực. Quý khách vui lòng liên hệ nhân viên để được hỗ trợ."
                    />
                )}

            </div>

        </div>
    );
}

function ResultCard({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 text-center">
            <div className="mb-4">{icon}</div>
            <h1 className="text-xl font-bold text-slate-800 mb-2">{title}</h1>
            <p className="text-sm text-slate-500">{desc}</p>
        </div>
    );
}
